import { action, API, controller } from "api";
import { useAppContext } from "context";
import { IconButton, Tooltip } from "@mui/material";
import { SwapHorizRounded } from "@mui/icons-material";
import { no_img } from "assets";
import { ToFormData } from "hooks";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Header logo + company/outlet label. Rendered by BOTH shells — the classic sidebar
// (components/body/Header.jsx) and the modern top nav (layout/TopNav/TopNavShell.jsx) —
// so this one component is the whole surface for "click the logo to go home".
const CompanyProfile = ({ light = false }) => {
  const { bizContext, openBizContext } = useAppContext();
  const { t } = useTranslation(["nav"]);
  const [values, setValues] = useState();

  useEffect(() => {
    if (bizContext && bizContext?.companyId) {
      API(controller.Company + action.BizContextDetails).post(
        ToFormData({ ...bizContext }),
        (res) => {
          res?.status === 200
            ? setValues({ ...res?.result })
            : console.error(res?.msg);
        },
      );
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizContext]);

  return (
    <div className="flex gap-1 items-center min-w-0">
      {/*
        A real <Link>, not a div with onClick: it gets keyboard focus, Enter, and
        ctrl/middle-click-to-new-tab for free.

        It targets "/" rather than "/home" ON PURPOSE. "/" is the per-user landing
        resolver in SiteRoute — it sends dashboard users to /home and everyone else to
        their first accessible module. /home is the dashboard route and is not even
        registered for a user without dashboard access, so hardcoding it here would put
        a cashier on a 404. Same reason the old dashboard-gated Home button was removed
        from TopNavShell, and it matches NavShortcuts' Home icon (`to="/"`).
      */}
      <Link
        to="/"
        title={t("nav:home", "Home")}
        aria-label={t("nav:home", "Home")}
        className="flex gap-2 items-center px-2 min-w-0 no-underline text-inherit rounded-[8px] hover:opacity-80 transition-opacity"
      >
        <img
          src={values?.companyImgUrl || no_img}
          alt="Company Logo"
          className="h-[40px] object-contain flex-shrink-0"
        />
        <div className={`flex flex-col min-w-0 flex-1 ${light ? "text-slate-700" : "text-white"}`}>
          <span className="truncate text-[14px] font-medium leading-tight">{values?.companyLabel}</span>
          <span className={`text-[12px] tracking-wider truncate leading-tight ${light ? "text-slate-400" : ""}`}>
            {values?.outletLabel}
          </span>
        </div>
      </Link>

      {/*
        Change branch. Deliberately a sibling of the Link rather than inside it —
        nesting a button in an anchor is invalid and swallows the keyboard.

        This exists because the branch was previously answerable ONCE, at login, and
        the only way to change the answer was to clear localStorage. Warehouse and
        back-office staff never open a till, so they had no other outlet signal at
        all — and the stock transfer screens ask "which branch am I?" on every list.
      */}
      <Tooltip title={t("nav:changeBranch", "Change branch")}>
        <IconButton
          size="small"
          onClick={openBizContext}
          aria-label={t("nav:changeBranch", "Change branch")}
          className={light ? "!text-slate-500" : "!text-white"}
        >
          <SwapHorizRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default CompanyProfile;
